/**
 * El aviso de FOTO REPETIDA de Validación: con qué otra marca comparte la foto una marca marcada.
 *
 * La bandera `foto_repetida` la pone el servidor (0314) cuando dos marcas tienen la misma huella
 * —el eTag que Storage calcula del contenido—, salvo que sean el mismo marcado reintentado. Es
 * antifraude: el 7-oct se encontraron 186 selfies de P&G copiadas a mano de una marca a otra, y
 * solo se vieron abriéndolas una por una.
 *
 * La bandera sola dice «esta foto ya se usó». Lo que hace falta para decidir es CUÁL es la otra:
 * no es lo mismo la salida de este mismo turno cargada con la foto del ingreso que la selfie de
 * otra persona en otra tienda. Por eso el detalle nombra la fecha y la persona.
 *
 * ⚠️ La otra marca puede no estar a la vista de quien valida (otra campaña, o una tienda que el
 * supervisor no revisa): la lectura va con los permisos de cada uno. En ese caso se dice eso, y no
 * se inventa nada.
 */

export interface MarcaConFoto {
  id: string;
  foto_huella: string | null;
  kind: 'ingreso' | 'salida';
  assignment: {
    id: string;
    worker_id?: string;
    worker: { first_names: string; last_names: string } | null;
    position: { date: string; pdv: { name: string } | null } | null;
  };
}

/** Lo mínimo de la marca marcada que hace falta para encontrar a sus compañeras. */
export interface MarcaMarcada {
  id: string;
  foto_huella: string | null;
  assignment: { id: string };
}

/** Cuántas compañeras se nombran antes de resumir el resto en «y N más». */
const MAXIMO_NOMBRADAS = 3;

/** `yyyy-MM-dd` → `dd/MM/yyyy`, sin pasar por `Date` (que correría el día por la zona horaria). */
function fechaCorta(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return d && m && y ? `${d}/${m}/${y}` : iso;
}

/** Las OTRAS marcas con la misma foto, de la más vieja a la más nueva. */
export function otrasConLaMismaFoto(
  marca: MarcaMarcada, todas: readonly MarcaConFoto[],
): MarcaConFoto[] {
  if (marca.foto_huella === null) return [];
  return todas
    .filter((o) => o.id !== marca.id && o.foto_huella === marca.foto_huella)
    .sort((a, b) => {
      const fa = a.assignment.position?.date ?? '';
      const fb = b.assignment.position?.date ?? '';
      if (fa !== fb) return fa < fb ? -1 : 1;
      if (a.kind !== b.kind) return a.kind === 'ingreso' ? -1 : 1;
      return a.id < b.id ? -1 : 1;
    });
}

function describirUna(marca: MarcaMarcada, o: MarcaConFoto): string {
  if (o.assignment.id === marca.assignment.id) {
    return o.kind === 'ingreso' ? 'el ingreso de este mismo turno' : 'la salida de este mismo turno';
  }
  const w = o.assignment.worker;
  const nombre = w ? `${w.first_names} ${w.last_names}`.trim() : 'una persona que no tienes a la vista';
  const partes = [nombre];
  if (o.assignment.position?.date) partes.push(fechaCorta(o.assignment.position.date));
  partes.push(o.kind === 'ingreso' ? 'ingreso' : 'salida');
  if (o.assignment.position?.pdv?.name) partes.push(o.assignment.position.pdv.name);
  return partes.join(' · ');
}

/**
 * La línea que va debajo de la insignia «Foto repetida: es la misma que otra marca».
 *
 * `todas` en `null` quiere decir que la lectura de las compañeras falló: se dice así, porque «no
 * está a tu vista» sería afirmar algo que no se sabe.
 */
export function detalleFotoRepetida(
  marca: MarcaMarcada, todas: readonly MarcaConFoto[] | null,
): string {
  if (todas === null) return 'No se pudo cargar cuál es la otra marca. Vuelve a buscar para verla.';
  const otras = otrasConLaMismaFoto(marca, todas);
  if (otras.length === 0) {
    return 'La otra marca no está a tu vista: es de una tienda o una campaña que no revisas.';
  }
  const nombradas = otras.slice(0, MAXIMO_NOMBRADAS).map((o) => describirUna(marca, o));
  const resto = otras.length - nombradas.length;
  return `Igual a: ${nombradas.join('; ')}${resto > 0 ? ` (y ${resto} más)` : ''}.`;
}
